import React, { useEffect, useState } from 'react';
import { func, shape, string } from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import template from 'url-template';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';

import { debounce, has } from 'lodash';

import { BLANK_RULE_TEMPLATE_ID } from '../../helpers/rules';
import { enqueueSnackbar } from '../../actions/applicationMessageActions';
import { getErrorMessage } from '../../helpers/errors';
import lambdaBaseApi from '../../apis/lambda';
import RulesForm, { RulesFormSchema } from '../../components/RulesForm';
import { saveRule } from '../../actions/ruleActions';
import { syncSensorsXml } from '../../helpers/sensors';
import useApi from '../../hooks/useApi';
import useFormId from '../../hooks/useFormId';
import useIsMounted from '../../hooks/useIsMounted';
import useRule from '../../hooks/useRule';
import { INITIAL_FAULT_LOGIC_XML, INITIAL_FORMULA_XML } from '../../helpers/blockly/xmlInitialState';

const DEFAULT_LIMIT = 10000;
const RuleTemplatesUrl = template.parse('/rules/templates{?limit}');
const RuleUrl = template.parse('/rules/{id}/edit');

const ModifyRulePage = ({ location, match, history, tablePageSize, ...props }) => {
    const ruleId = match.params.id;
    const formId = useFormId('rule');
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const [redirectTo, setRedirectTo] = useState(null);
    const companyInfo = useSelector(
        ({ companyInfo }) => companyInfo
    );
    const [persistedRule, getRule] = useRule();
    const [ruleTemplates, getRuleTemplates] = useApi(() => ({
        adapter: lambdaBaseApi,
        url:     RuleTemplatesUrl.expand({
            limit: DEFAULT_LIMIT
        })
    }));
    const [assignedRule, assignRule] = useApi(
        ({ enabled, ruleTemplateId: id }) => ({
            adapter: lambdaBaseApi,
            data:    { id, enabled },
            method:  'POST',
            url:     '/rules/assignment'
        })
    );
    const {
        errors,
        handleBlur,
        handleReset,
        handleSubmit,
        isSubmitting: isFormikSubmitting,
        setFieldValue,
        touched,
        values,
        ...formik
    } = useFormik({
        enableReinitialize: true,
        initialValues:      persistedRule.data,
        onSubmit:           debounce(handleFormikSubmit, 1000),
        validateOnChange:   false,
        validationSchema:   RulesFormSchema
    });

    /**
     * Fetch the desired rule when the component mounts.
     */
    useEffect(() => {
        getRule(ruleId);

        if (!ruleId) {
            getRuleTemplates();
        }
        return () => {
            if (!ruleId) {
                ruleTemplates.cancel();
            }
        };
    }, []);

    /**
     * Report any network errors from loading the rule to the user.
     */
    useEffect(() => {
        if (!persistedRule.error) {
            return;
        }
        dispatch(enqueueSnackbar(persistedRule.error, 'error'));
        handleCancel();
    }, [persistedRule.error]);

    /**
     * Handle the Rule Assignment lifecycle
     */
    useEffect(() => {
        if (!assignedRule.complete) {
            return;
        }
        if (assignedRule.error) {
            dispatch(enqueueSnackbar(assignedRule.error, 'error'));
            return;
        }
        dispatch(
            enqueueSnackbar('Rule Template successfully assigned.', 'success')
        );
        redirectToRules();
        return () => {
            assignedRule.cancel();
        };
    }, [assignedRule.complete, assignedRule.error]);

    function handleCancel() {
        redirectToRules();
    }

    /**
     * The Rules form is configured to only validate when the user navigates
     * from one field to the next (`onBlur`). However, this behavior feels
     * odd for certain field types, namely checkboxes and switches. This
     * `onChange` handler performs the default Formik `onChange` behavior while
     * also queueing up an explicit call to `validateForm` whenever a checkbox
     * value is changed so the user gets immediate feedback as to whether or
     * not their interaction has invalidated the form.
     */
    function handleChange(event) {
        formik.handleChange(event);
        if (event.target.type === 'checkbox') {
            // Validate the form in the next event loop to give Formik a chance
            // to update its own internal state.
            requestAnimationFrame(() => formik.validateForm());
        }
    }

    /**
     * There are certain fields that need to be able to set multiple values in
     * one form interaction. Because React re-uses a single `SyntheticEvent`,
     * it is not possible to manually trigger two `onChange` events. Instead,
     * this form introduces a pseudo "onPatch" handler that provides the means
     * to change multiple values in one go. In order to update Formik with the
     * new group of values, this handler uses the stored reference to Formik's
     * `setFieldValue` function and manually calls it for each key of the
     * provided changes, thus accomplishing the goal of changing multiple values
     * in one interaction.
     *
     * This form is configured to validate the form on field blur events, so
     * no explicit field validation is required when updating the batch of
     * values. However, there are some properties that are not defined by
     * elements that support a traditional "blur" interaction. In these cases,
     * an second options arguments allows the caller to trigger an explicit
     * form re-validation once the patch has been applied.
     */
    async function handlePatch(patch, { validateForm } = {}) {
        if (has(patch, 'sensors')) {
            if (patch.sensors && patch.sensors.length) {
                patch = {
                    ...patch,
                    formulaXml: syncSensorsXml(
                        patch.sensors,
                        patch.formulaXml || values.formulaXml
                    ),
                    faultLogicXml: syncSensorsXml(
                        patch.sensors,
                        patch.faultLogicXml || values.faultLogicXml
                    )
                };
            }
            if (patch.sensors && !patch.sensors.length) {
                patch = {
                    ...patch,
                    formulaXml: syncSensorsXml(
                        patch.sensors,
                        INITIAL_FORMULA_XML
                    ),
                    faultLogicXml: syncSensorsXml(
                        patch.sensors,
                        INITIAL_FAULT_LOGIC_XML
                    )
                };
            }
        }
        await Promise.all(Object.entries(patch).map(([fieldName, value]) => {
            return setFieldValue(fieldName, value);
        }));
        if (validateForm) {
            formik.validateForm();
        }
    }

    function redirectToRules() {
        const params = new URLSearchParams(location.search);
        params.append('pageSize', tablePageSize);
        params.delete('ruleTemplateId');

        if (history.location.state && history.location.state.returnUrl) {
            setRedirectTo({
                pathname: history.location.state.returnUrl,
                search:   `?${params.toString()}`
            });
        } else {
            setRedirectTo({
                pathname: '/rules',
                search:   `?${params.toString()}`
            });
        }
    }

    function redirectToSavedRule(newRule) {
        if (newRule.data.id === ruleId) {
            // Not a newly created rule
            return;
        }
        const params = new URLSearchParams(location.search);
        params.delete('ruleTemplateId');
        setRedirectTo({
            pathname: RuleUrl.expand(newRule.data),
            search:   `?${params.toString()}`
        });
    }

    /**
     * ModifyRulePage contains a Modal with a `form` element. While this form
     * element is not within the DOM tree, React's `SyntheticEvent` bubbles up
     * as if it was due to the Portal implementation. This causes both form's
     * submit handlers from firing causing some confusion. This submit handler
     * checks to see if the submit event's target is the overall Rule form and
     * essentially "cancels" the submit event if it is for another form.
     *
     * See also:
     *
     * *  https://reactjs.org/docs/portals.html#event-bubbling-through-portals
     */
    function handleFormSubmit(event) {
        if (event.type === 'submit' && event.target.id === formId) {
            handleSubmit(event);
            return;
        }
        event.preventDefault();
    }

    async function updateRule(rule, actions) {
        actions.setSubmitting(true);
        try {
            await assignRule(rule);
        } catch (error) {
            dispatch(enqueueSnackbar(getErrorMessage(error), 'error'));
        } finally {
            if (isMounted.current) {
                actions.setSubmitting(false);
            }
        }
    }

    async function saveNewRule(rule, actions) {
        try {
            const newRule = await dispatch(saveRule(rule));
            dispatch(enqueueSnackbar('Rule saved successfully.', 'success'));
            redirectToSavedRule(newRule);
        } catch (error) {
            dispatch(enqueueSnackbar(getErrorMessage(error), 'error'));
        } finally {
            if (isMounted.current) {
                actions.setSubmitting(false);
            }
        }
    }

    /**
     * Handle a Rule's Form submission
     */
    async function handleFormikSubmit(rule, actions) {
        const { ruleTemplateId } = rule;
        if (
            ruleTemplateId &&
            ruleTemplateId !== BLANK_RULE_TEMPLATE_ID && !rule.custom
        ) {
            updateRule(rule, actions);
        } else {
            saveNewRule(rule, actions);
        }
    }

    if (redirectTo) {
        return (
            <Redirect push to={redirectTo} />
        );
    }

    return (
        <RulesForm
            assignedRule={assignedRule}
            companyInfo={companyInfo}
            errors={errors}
            id={formId}
            isFormikSubmitting={isFormikSubmitting}
            onFormikSubmit={handleFormikSubmit}
            onBlur={handleBlur}
            onCancel={handleCancel}
            onChange={handleChange}
            onPatch={handlePatch}
            onReset={handleReset}
            onSubmit={handleFormSubmit}
            rule={persistedRule}
            ruleTemplates={ruleTemplates}
            selectedTab={match.params.tab}
            setFieldValue={setFieldValue}
            touched={touched}
            values={values}
        />
    );
};

ModifyRulePage.propTypes = {
    history: shape({
        push: func
    }).isRequired,
    match: shape({
        params: shape({
            id: string
        }).isRequired
    }).isRequired
};

const mapStateToProps = state => ({
    tablePageSize: state.rules.legacyRuleTablePageSize
});

export default withRouter(connect(mapStateToProps, null)(ModifyRulePage));
